import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CloseButton,
  Container,
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Input,
  Textarea,
  Button,
  NativeSelect,
  Flex,
  Alert,
  useDisclosure,
  Field,
  Dialog,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiPlus, FiEye, FiSave, FiCopy } from 'react-icons/fi'
import { FilterGroupCard, LiveCountBadge, PreviewDrawer, toEvaluationRequest } from '../components/listBuilder'
import {
  useSavedSearchDetail,
  useCreateSavedSearch,
  useUpdateSavedSearch,
  useSavedSearchFieldMetadata,
  useSavedSearchTags,
  useGenerateListFromSearch,
} from '../hooks/useSavedSearch'
import { useIsMobile } from '../hooks/useIsMobile'
import { extractApiMessage } from '../lib/apiError'
import { ResponsiveModal, StickyActionFooter } from '../components/shared'
import type { FilterGroup, FilterOperator, ListRefreshStrategy } from '../types/contactList'
import { uuid } from '../lib/uuid'

export function SearchBuilderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = !!id
  const isMobile = useIsMobile()

  // Load metadata
  const { data: fields = [] } = useSavedSearchFieldMetadata()
  const { data: tags = [] } = useSavedSearchTags()

  // Load existing search if editing
  const { data: existingSearch, isLoading: isLoadingSearch } = useSavedSearchDetail(id)

  // Mutations
  const createMutation = useCreateSavedSearch()
  const updateMutation = useUpdateSavedSearch(id ?? '')
  const generateListMutation = useGenerateListFromSearch(id ?? '')

  // Preview drawer
  const { open: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure()

  // Generate list modal
  const { open: isGenerateOpen, onOpen: onGenerateOpen, onClose: onGenerateClose } = useDisclosure()
  const [generateLabel, setGenerateLabel] = useState('')
  const [generateStrategy, setGenerateStrategy] = useState<ListRefreshStrategy>('Manual')

  // Form state
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([
    createEmptyGroup(0),
  ])

  // Populate when editing
  useEffect(() => {
    if (existingSearch) {
      setLabel(existingSearch.label)
      setDescription(existingSearch.description ?? '')
      if (existingSearch.filterGroups.length > 0) {
        setFilterGroups(
          existingSearch.filterGroups.map(g => ({
            ...g,
            filters: g.filters.map(f => ({
              ...f,
              id: f.id || uuid(),
            })),
          }))
        )
      }
    }
  }, [existingSearch])

  const handleGroupChange = useCallback((index: number, updated: FilterGroup) => {
    setFilterGroups(prev => {
      const next = [...prev]
      next[index] = updated
      return next
    })
  }, [])

  const handleRemoveGroup = useCallback((index: number) => {
    setFilterGroups(prev => {
      const next = prev.filter((_, i) => i !== index)
      return next.map((g, i) => ({ ...g, groupIndex: i }))
    })
  }, [])

  const handleAddGroup = useCallback(() => {
    setFilterGroups(prev => [...prev, createEmptyGroup(prev.length)])
  }, [])

  const handleSaveTemplate = async () => {
    if (!label.trim()) {
      toaster.create({ title: 'Please enter a name', type: 'warning', duration: 3000 })
      return
    }

    const request = {
      label: label.trim(),
      description: description.trim() || undefined,
      filterGroups: filterGroups.map(g => ({
        groupIndex: g.groupIndex,
        filters: g.filters
          .filter(f => f.fieldName)
          .map((f, i) => ({
            fieldName: f.fieldName,
            operator: f.operator,
            value: f.value,
            displayOrder: i,
          })),
      })),
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync(request)
        toaster.create({ title: 'Template updated', type: 'success', duration: 3000 })
      } else {
        const result = await createMutation.mutateAsync(request)
        toaster.create({ title: 'Template created', type: 'success', duration: 3000 })
        navigate(`/dashboard/searches/${result.id}/edit`, { replace: true })
      }
    } catch (err) {
      toaster.create({ title: 'Failed to save template', description: extractApiMessage(err, 'Please try again.'), type: 'error', duration: 5000 })
    }
  }

  const handleGenerateList = async () => {
    if (!generateLabel.trim()) {
      toaster.create({ title: 'Please enter a list name', type: 'warning', duration: 3000 })
      return
    }

    try {
      const result = await generateListMutation.mutateAsync({
        label: generateLabel.trim(),
        refreshStrategy: generateStrategy,
      })
      toaster.create({ title: 'Contact list created', type: 'success', duration: 3000 })
      onGenerateClose()
      navigate(`/dashboard/lists/${result.contactListId}/edit`)
    } catch (err) {
      toaster.create({ title: 'Failed to generate list', description: extractApiMessage(err, 'Please try again.'), type: 'error', duration: 5000 })
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending
  const previewRequest = toEvaluationRequest(filterGroups)

  if (isEditing && isLoadingSearch) {
    return (
      <Container maxW="container.lg" py={8}>
        <Text color="gray.500">Loading search template...</Text>
      </Container>
    )
  }

  return (
    <Container maxW="container.lg" px={{ base: 4, md: 8 }} py={{ base: 4, md: 8 }}>
      <VStack gap={6} align="stretch" pb={{ base: '96px', md: 8 }}>
        {/* Header */}
        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
          <Box>
            <Heading size="lg">{isEditing ? 'Edit Search Template' : 'New Search Template'}</Heading>
            <Text color="gray.500" mt={1}>
              Build a reusable filter template to generate contact lists
            </Text>
          </Box>
        </Flex>

        {/* Live count + Preview (sticky under top bar on mobile) */}
        <Box
          position={{ base: 'sticky', md: 'static' }}
          top={{ base: 'calc(68px + env(safe-area-inset-top, 0px))', md: 'auto' }}
          zIndex={{ base: 8, md: 'auto' }}
          bg={{ base: 'white', md: 'transparent' }}
          py={2}
        >
          <HStack gap={3} justify={{ base: 'space-between', md: 'flex-end' }}>
            <LiveCountBadge filterGroups={filterGroups} source="saved-searches" />
            <Button
              variant="outline"
              size="sm"
              onClick={onPreviewOpen}
              disabled={!previewRequest}><FiEye />Preview
                          </Button>
          </HStack>
        </Box>

        {/* Metadata Card */}
        <Box bg="surface" p={6} borderRadius="lg" shadow="md" borderWidth="1px" borderColor="gray.200">
          <VStack gap={4} align="stretch">
            <Field.Root required>
              <Field.Label>Template Name</Field.Label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Active Voters in Virginia"
              />
            </Field.Root>
            <Field.Root>
              <Field.Label>Description</Field.Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description of this template's purpose..."
                rows={2}
              />
            </Field.Root>
          </VStack>
        </Box>

        {/* Filter Groups */}
        <Box>
          <Heading size="md" mb={4}>Filter Groups</Heading>
          {fields.length === 0 && (
            <Alert.Root status="info" borderRadius="md">
              <Alert.Indicator />
              Loading available filter fields...
            </Alert.Root>
          )}
          <VStack gap={4} align="stretch">
            {filterGroups.map((group, index) => (
              <Box key={group.groupIndex}>
                {index > 0 && (
                  <Flex justify="center" my={2}>
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color="orange.500"
                      bg="orange.50"
                      px={4}
                      py={1}
                      borderRadius="full"
                    >
                      OR
                    </Text>
                  </Flex>
                )}
                <FilterGroupCard
                  group={group}
                  fields={fields}
                  tags={tags}
                  onChange={(updated) => handleGroupChange(index, updated)}
                  onRemove={() => handleRemoveGroup(index)}
                  canRemove={filterGroups.length > 1}
                />
              </Box>
            ))}

            <Button
              variant="outline"
              colorPalette="orange"
              onClick={handleAddGroup}
              alignSelf="flex-start"><FiPlus />Add OR group
                          </Button>
          </VStack>
        </Box>

        {/* Action Bar */}
        {isMobile ? (
          <StickyActionFooter>
            <Button
              colorPalette="brand"
              onClick={handleSaveTemplate}
              loading={isSaving}
              disabled={!label.trim()}
            >
              {isEditing ? 'Save Template' : 'Create Template'}
            </Button>
            {isEditing && (
              <Button
                colorPalette="green"
                variant="outline"
                onClick={() => {
                  setGenerateLabel(label ? `${label} — List` : '')
                  onGenerateOpen()
                }}
              >
                Generate New List
              </Button>
            )}
          </StickyActionFooter>
        ) : (
          <HStack gap={4} pt={4} borderTopWidth="1px" borderColor="gray.200">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard/lists')}
            >
              Cancel
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleSaveTemplate}
              loading={isSaving}
              disabled={!label.trim()}><FiSave />{isEditing ? 'Save Template' : 'Create Template'}</Button>
            {isEditing && (
              <Button
                colorPalette="green"
                variant="outline"
                onClick={() => {
                  setGenerateLabel(label ? `${label} — List` : '')
                  onGenerateOpen()
                }}><FiCopy />Generate New List
                              </Button>
            )}
          </HStack>
        )}
      </VStack>

      {/* Preview Drawer */}
      <PreviewDrawer
        isOpen={isPreviewOpen}
        onClose={onPreviewClose}
        request={previewRequest}
        source="saved-searches"
      />

      {/* Generate List Modal */}
      <ResponsiveModal size="lg" isOpen={isGenerateOpen} onClose={onGenerateClose} isCentered>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Generate New Contact List</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={4}>
                <Text fontSize="sm" color="gray.600">
                  Create a new contact list from this template's current filters.
                </Text>
                <Field.Root required>
                  <Field.Label>List Name</Field.Label>
                  <Input
                    value={generateLabel}
                    onChange={(e) => setGenerateLabel(e.target.value)}
                    placeholder="e.g., VA Active Voters — Feb 2026"
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label>Refresh Strategy</Field.Label>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      value={generateStrategy}
                      onChange={(e) => setGenerateStrategy(e.target.value as ListRefreshStrategy)}>
                      <option value="Dynamic">Dynamic (always live)</option>
                      <option value="Manual">Manual (refresh on demand)</option>
                      <option value="AutoDaily">Auto Daily</option>
                      <option value="Locked">Locked (snapshot)</option>
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button variant="ghost" mr={3} onClick={onGenerateClose}>
                Cancel
              </Button>
              <Button
                colorPalette="green"
                onClick={handleGenerateList}
                loading={generateListMutation.isPending}
                disabled={!generateLabel.trim()}
              >
                Generate List
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </Container>
  );
}

function createEmptyGroup(index: number): FilterGroup {
  return {
    groupIndex: index,
    filters: [
      {
        id: uuid(),
        fieldName: '',
        operator: 'Equals' as FilterOperator,
        value: undefined,
        displayOrder: 0,
      },
    ],
  }
}
